/**
 * Volume Profile calculation service.
 * Implements TradingView-style logic per:
 * https://www.tradingview.com/support/solutions/43000502040-volume-profile-indicators-basic-concepts/
 *
 * Uses lower timeframe bars: close >= open = up volume, close < open = down volume.
 * Computes POC (Point of Control), Value Area (VAH/VAL), and histogram rows.
 */

/** @typedef {{ time: number; open: number; high: number; low: number; close: number; volume: number }} Bar */

/** @typedef {'total'|'upDown'|'delta'} VolumeDisplayType */

/** @typedef {{ price: number; total: number; up: number; down: number; delta: number }} VolumeRow */

/** @typedef {{ rows: VolumeRow[]; poc: number; vah: number; val: number; profileHigh: number; profileLow: number }} VolumeProfileResult */

const DEFAULT_VALUE_AREA_PCT = 70;
/** Default histogram bucket size — 5¢ keeps row counts manageable for wide ranges. */
export const DEFAULT_VOLUME_PROFILE_TICK_SIZE = 0.05;
const TIMEFRAME_SEQUENCE = [
  { interval: 1, unit: 'Minute' },
  { interval: 5, unit: 'Minute' },
  { interval: 15, unit: 'Minute' },
  { interval: 30, unit: 'Minute' },
  { interval: 60, unit: 'Minute' },
  { interval: 240, unit: 'Minute' },
  { interval: 1, unit: 'Daily' },
];

/**
 * Choose lower timeframe for VP calculation.
 * Uses bars count < 5000 heuristic (per TradingView).
 * @param {number} barCount - Number of bars in the range
 * @returns {{ interval: number; unit: string }}
 */
export function chooseLowerTimeframe(barCount) {
  const idx = TIMEFRAME_SEQUENCE.findIndex(() => barCount < 5000);
  return TIMEFRAME_SEQUENCE[Math.max(0, idx)] || TIMEFRAME_SEQUENCE[0];
}

/**
 * Distribute bar volume across price levels (high-low range).
 * Up volume: close >= open. Down volume: close < open.
 * @param {Bar} bar
 * @returns {{ price: number; up: number; down: number }[]}
 */
function distributeBarToLevels(bar, tickSize = DEFAULT_VOLUME_PROFILE_TICK_SIZE) {
  const open = parseFloat(bar.open);
  const high = parseFloat(bar.high);
  const low = parseFloat(bar.low);
  const close = parseFloat(bar.close);
  const vol = parseFloat(bar.volume) || 0;
  if (!Number.isFinite(open) || !Number.isFinite(high) || !Number.isFinite(low) || !Number.isFinite(close) || vol <= 0) {
    return [];
  }
  const isUp = close >= open;
  const priceRange = high - low;
  if (priceRange <= 0) {
    return [{ price: (high + low) / 2, up: isUp ? vol : 0, down: isUp ? 0 : vol }];
  }
  const levels = [];
  const numTicks = Math.max(1, Math.round(priceRange / tickSize));
  const volPerLevel = vol / numTicks;
  for (let i = 0; i <= numTicks; i++) {
    const price = low + (priceRange * i) / numTicks;
    levels.push({
      price: Math.round(price / tickSize) * tickSize,
      up: isUp ? volPerLevel : 0,
      down: isUp ? 0 : volPerLevel,
    });
  }
  return levels;
}

/**
 * Build volume profile from bars.
 * @param {Bar[]} bars - Lower timeframe bars (e.g. 1-min)
 * @param {Object} opts
 * @param {number} [opts.tickSize=0.05]
 * @param {number} [opts.minPrice]
 * @param {number} [opts.maxPrice]
 * @param {number} [opts.valueAreaPct=70]
 * @param {number} [opts.rowCount] - Optional fixed row count
 * @returns {VolumeProfileResult}
 */
export function computeVolumeProfile(bars, opts = {}) {
  const tickSize = opts.tickSize ?? DEFAULT_VOLUME_PROFILE_TICK_SIZE;
  const minPrice = opts.minPrice;
  const maxPrice = opts.maxPrice;
  const valueAreaPct = opts.valueAreaPct ?? DEFAULT_VALUE_AREA_PCT;
  const rowCount = opts.rowCount;

  const priceToVol = new Map();

  for (const bar of bars || []) {
    const levels = distributeBarToLevels(bar, tickSize);
    for (const { price, up, down } of levels) {
      if (minPrice != null && price < minPrice) continue;
      if (maxPrice != null && price > maxPrice) continue;
      const key = Math.round(price / tickSize) * tickSize;
      const existing = priceToVol.get(key) || { up: 0, down: 0 };
      priceToVol.set(key, {
        up: existing.up + up,
        down: existing.down + down,
      });
    }
  }

  const rows = [];
  for (const [price, v] of priceToVol) {
    const total = v.up + v.down;
    rows.push({
      price: parseFloat(price),
      total,
      up: v.up,
      down: v.down,
      delta: v.up - v.down,
    });
  }

  if (rows.length === 0) {
    return { rows: [], poc: 0, vah: 0, val: 0, profileHigh: 0, profileLow: 0 };
  }

  rows.sort((a, b) => a.price - b.price);

  const totalVol = rows.reduce((s, r) => s + r.total, 0);
  const profileHigh = rows[rows.length - 1].price;
  const profileLow = rows[0].price;

  let pocRow = rows[0];
  for (const r of rows) {
    if (r.total > pocRow.total) pocRow = r;
  }
  const poc = pocRow.price;

  const targetVol = (valueAreaPct / 100) * totalVol;
  let vaVol = pocRow.total;
  let vah = poc;
  let val = poc;
  let aboveIdx = rows.findIndex((r) => r.price === poc) + 1;
  let belowIdx = rows.findIndex((r) => r.price === poc) - 1;

  while (vaVol < targetVol && (aboveIdx < rows.length || belowIdx >= 0)) {
    const aboveVol = aboveIdx < rows.length ? rows[aboveIdx].total : 0;
    const belowVol = belowIdx >= 0 ? rows[belowIdx].total : 0;
    if (aboveVol >= belowVol && aboveIdx < rows.length) {
      const r = rows[aboveIdx];
      if (vaVol + r.total <= targetVol) {
        vaVol += r.total;
        vah = r.price;
        aboveIdx++;
      } else break;
    } else if (belowIdx >= 0) {
      const r = rows[belowIdx];
      if (vaVol + r.total <= targetVol) {
        vaVol += r.total;
        val = r.price;
        belowIdx--;
      } else break;
    } else break;
  }

  let result = rows;
  if (rowCount != null && rowCount > 0 && rows.length > rowCount) {
    const priceSpan = profileHigh - profileLow || tickSize;
    const bucketSize = priceSpan / rowCount;
    const buckets = new Map();
    for (const r of rows) {
      const bucketPrice = profileLow + Math.floor((r.price - profileLow) / bucketSize) * bucketSize + bucketSize / 2;
      const key = Math.round(bucketPrice / tickSize) * tickSize;
      const b = buckets.get(key) || { price: key, total: 0, up: 0, down: 0 };
      b.total += r.total;
      b.up += r.up;
      b.down += r.down;
      buckets.set(key, b);
    }
    result = Array.from(buckets.values()).map((b) => ({
      ...b,
      delta: b.up - b.down,
    }));
    result.sort((a, b) => a.price - b.price);
  }

  return {
    rows: result,
    poc,
    vah,
    val,
    profileHigh,
    profileLow,
  };
}

/**
 * Compute session volume profiles (one per session/day).
 * @param {Bar[]} bars - Chart bars (e.g. daily)
 * @param {Bar[]} lowerBars - Lower timeframe bars for the same period
 * @param {Object} opts
 * @returns {Map<number, VolumeProfileResult>} Session start time -> profile
 */
export function computeSessionProfiles(bars, lowerBars, opts = {}) {
  const result = new Map();
  if (!bars?.length || !lowerBars?.length) return result;

  const dayMs = 24 * 60 * 60 * 1000;
  const getDayStart = (t) => {
    const d = new Date(t * 1000);
    d.setHours(0, 0, 0, 0);
    return Math.floor(d.getTime() / 1000);
  };

  for (const bar of bars) {
    const t = bar.time;
    const dayStart = getDayStart(t);
    const dayEnd = dayStart + 24 * 3600;
    const sessionBars = lowerBars.filter((b) => b.time >= dayStart && b.time < dayEnd);
    if (sessionBars.length === 0) continue;
    const profile = computeVolumeProfile(sessionBars, opts);
    if (profile.rows.length > 0) {
      result.set(dayStart, profile);
    }
  }
  return result;
}
